import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { AppShell } from '../design-system/shell/app-shell';
import { NavIdentity } from '../design-system/shell/nav';
import { PageHeader } from '../design-system/shell/page-header';
import { ActionButton } from '../design-system/concept/action-button';
import { COACH_NAV } from '../components/dashboard/nav-entries';
import { SignInPanel } from '../design-system/shell/sign-in-panel';
import { LoadErrorCard } from '../design-system/concept/load-error-card';
import { describeLoadError, type LoadErrorCopy } from '../lib/load-error';

import { AuthProvider, getProfiles } from '../lib/http-service';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/date-utils';
import { formatImage } from '../lib/image-utils';
import type { ClientData } from '../lib/interfaces';
import { noun } from '../lib/features';

const Avatar: React.FC<{ client: ClientData }> = ({ client }) => {
  const initials = `${client.firstName?.[0] ?? ''}${client.lastName?.[0] ?? ''}`.toUpperCase();
  return client.profileImage ? (
    <img
      src={formatImage(client.profileImage)}
      alt=""
      className="h-10 w-10 shrink-0 rounded-full object-cover"
    />
  ) : (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-semibold text-accent-foreground">
      {initials || '—'}
    </span>
  );
};

/**
 * The coach's roster.
 *
 * Rewritten off the gray gradient it shipped with — that page set
 * `from-gray-50`, `text-gray-900` and friends directly, so none of the
 * concept's tokens reached it and it read as a different product from the
 * dashboard it links into.
 */
const ClientSelection = () => {
  const history = useHistory();
  const [clients, setClients] = useState<ClientData[]>([]);
  const [query, setQuery] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  // Without this a failed request left "Loading clients…" on screen for ever.
  const [loadError, setLoadError] = useState<LoadErrorCopy | null>(null);
  const [attempt, setAttempt] = useState(0);
  const { isAuthenticated, isLoading, getAccessTokenSilently, signIn, user } =
    useAuth();
  const [auth] = useState(() => new AuthProvider(getAccessTokenSilently));

  useEffect(() => {
    if (!isAuthenticated) return;
    let live = true;
    getProfiles(auth).then(
      (data) => {
        if (!live) return;
        setClients(data);
        setIsLoaded(true);
      },
      (e) => live && setLoadError(describeLoadError(e)),
    );
    return () => {
      live = false;
    };
  }, [isAuthenticated, attempt]);

  const retry = () => {
    setLoadError(null);
    setAttempt((n) => n + 1);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q),
    );
  }, [clients, query]);

  if (!isAuthenticated) {
    return (
      <SignInPanel
        state={isLoading ? 'checking' : 'signed-out'}
        subject="clients"
        onSignIn={() => signIn()}
      />
    );
  }

  return (
    <AppShell
      entries={COACH_NAV}
      active="clients"
      onSelect={(k) => history.push(k === 'overview' ? '/overview' : '/clients')}
      onHome={() => history.push('/overview')}
      hidePhoneTabs
      identity={
        <NavIdentity
          name={user?.name ?? 'Coach'}
          detail={isLoaded ? `${clients.length} ${noun.clients}` : 'Coach'}
          imageUrl={user?.picture}
        />
      }
    >
      <div className="mx-auto flex max-w-[1296px] flex-col gap-3.5 p-4 lg:p-7">
        <PageHeader
          eyebrow="Roster"
          title={noun.Clients}
          meta={isLoaded ? `${clients.length} in total` : undefined}
          actions={
            <ActionButton
              variant="primary"
              onClick={() => history.push('/create-client')}
              testId="new-client"
            >
              New {noun.client}
            </ActionButton>
          }
        />

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email"
          aria-label={`Search ${noun.clients}`}
          className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
        />

        {loadError ? (
          <LoadErrorCard
            testId="clients-error"
            title={`Could not load your ${noun.clients}`}
            message={loadError.message}
            detail={loadError.detail}
            onRetry={retry}
          />
        ) : !isLoaded ? (
          <p className="text-sm text-muted-foreground">Loading {noun.clients}…</p>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <p className="text-sm text-foreground-soft">
              {query
                ? `No ${noun.client} matches that search.`
                : `No ${noun.clients} yet.`}
            </p>
            <ActionButton
              variant="primary"
              className="mt-4"
              onClick={() => history.push('/create-client')}
            >
              Create the first one
            </ActionButton>
          </div>
        ) : (
          <div
            data-testid="client-roster"
            className="overflow-hidden rounded-lg border border-border bg-card"
          >
            {filtered.map((client, i) => (
              <button
                key={client.ID}
                type="button"
                onClick={() => history.push(`/dashboard/${client.ID}`)}
                className={`flex w-full items-center gap-3.5 px-5 py-3.5 text-left transition-colors hover:bg-shell ${
                  i > 0 ? 'border-t border-divider' : ''
                }`}
              >
                <Avatar client={client} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold">
                    {client.firstName} {client.lastName}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {client.email}
                  </span>
                </span>
                {/* The figures a coach scans the roster for, in the concept's
                    mono. Hidden on phone, where the row has no room. */}
                <span className="tabular hidden shrink-0 text-right font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground sm:block">
                  {client.age ? `${client.age} y` : '—'}
                  {client.height ? ` · ${client.height} cm` : ''}
                </span>
                <span className="tabular hidden w-[150px] shrink-0 text-right text-[11px] text-foreground-soft md:block">
                  {client.lastMeasure
                    ? `Last scan ${formatDate(new Date(client.lastMeasure))}`
                    : 'No scans yet'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default ClientSelection;
